import { lazy, useCallback } from 'react';

import classNames from 'classnames';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';

import { CoinBackSteps } from '../../features/coinbackTutorial/constants';
import CheckCoinBackWidget from '../../features/coinbackTutorial/tooltips/CheckCoinBackWidget';
import { useCoinBackTutorial } from '../../features/coinbackTutorial/useCoinBackTutorial';
import { AnimationKeys } from '../../stores/animation/settings/config';
import { useStores } from '../../stores/useStores';
import { devError } from '../../utils/log';

import styles from './CoinBackWidget.module.scss';
import CoinBackTimer from './components/CoinBackTimer';

const CoinBackWidgetBoosterBlueThunder = lazy(() => import('../SVGIcons/CoinBackWidgetBoosterBlueThunder'));
const AnimatingThunderRight = lazy(() => import('../SVGIcons/AnimatingThunderRight'));
const AnimatingThunderLeft = lazy(() => import('../SVGIcons/AnimatingThunderLeft'));

export const CoinBackWidget = observer(() => {
  const { coinBackStore, animationStore } = useStores();
  const { currentCoinBack, currentCoinBackBooster, checkIfBoosterActive, isNewPeriodWithCollectables } = coinBackStore;

  const { showCoinBackTutorial, tooltipStyles, setReferenceElement, setPopperElement } = useCoinBackTutorial(
    CoinBackSteps.CHECK_FEATURE,
    'bottom',
    [140, 15],
  );

  if (!currentCoinBack) {
    return null;
  }

  const { widgetImageUrl, endDate } = currentCoinBack;

  const onClickHandler = () => {
    if (showCoinBackTutorial) return;
    coinBackStore.openDialog('widget_click');
    coinBackStore.sendAnalytics(
      {
        feature_sub_type: 'coinback',
        event_action: 'click',
      },
      {
        triggered_by: 'click',
        click_location: 'lobby',
      },
    );
  };

  const handleTimerEnd = useCallback(async () => {
    try {
      coinBackStore.setRunningAnimation(null);
      await animationStore.loadImages(AnimationKeys.SAFE_OPEN);
      await coinBackStore.fetch();
      coinBackStore.closeDialog();

      if (currentCoinBackBooster && dayjs(currentCoinBackBooster.endDate).isBefore(dayjs())) {
        coinBackStore.setCurrentCoinBackBooster(null);
      } else {
        coinBackStore.startDialogs('CoinBack');
      }
    } catch (error) {
      devError('Error CoinBack handling end', error);
    }
  }, [animationStore, coinBackStore, currentCoinBackBooster]);

  const onTimerEnd = useCallback(() => {
    // Random delay between 1 and 10 seconds to reduce server load
    const randomDelayMS = (Math.floor(Math.random() * (10 - 1 + 1)) + 1) * 1000;

    setTimeout(() => {
      void handleTimerEnd();
    }, randomDelayMS);
  }, [handleTimerEnd]);

  const isBoosterActive = checkIfBoosterActive(currentCoinBackBooster);

  const getWidgetImageUrl = () => {
    if (isBoosterActive) {
      return currentCoinBackBooster.widgetImageUrl || currentCoinBack.defaultBoosterWidgetImageUrl;
    }

    return widgetImageUrl;
  };

  return (
    <>
      <div
        className={classNames('home-widget', styles.coinBackWidgetContainer, {
          'coinback-tutorial': showCoinBackTutorial,
        })}
      >
        <div
          className={classNames(styles.coinBackWidget, 'circle-parent', {
            [styles.tutorialOpen]: showCoinBackTutorial,
          })}
          onClick={onClickHandler}
        >
          <div
            ref={setReferenceElement}
            className={classNames(styles.circle, { [styles.boosterActive]: isBoosterActive && !showCoinBackTutorial })}
          />
          {isBoosterActive && <CoinBackWidgetBoosterBlueThunder className={styles.boosterThunder} />}
          {isBoosterActive && (
            <AnimatingThunderRight className={classNames(styles.animatingThunder, styles.animatingThunderRight)} />
          )}
          {isBoosterActive && (
            <AnimatingThunderLeft className={classNames(styles.animatingThunder, styles.animatingThunderLeft)} />
          )}
          <img
            alt="CoinBack"
            className={classNames(styles.widgetImage, {
              [styles.booster]: isBoosterActive,
            })}
            loading="lazy"
            src={getWidgetImageUrl()}
          />
          {isBoosterActive && (
            <span
              className={classNames(styles.boosterMultiplier, {
                [styles.nonInteger]: !Number.isInteger(currentCoinBackBooster.multiplier),
              })}
            >
              X{currentCoinBackBooster.multiplier}
            </span>
          )}
          {
            <CoinBackTimer
              className="widget-timer"
              showDefault={isNewPeriodWithCollectables}
              timer={isBoosterActive ? currentCoinBackBooster.endDate : endDate}
              onCompleted={onTimerEnd}
            />
          }
        </div>
        {showCoinBackTutorial && (
          <CheckCoinBackWidget setPopperElement={setPopperElement} tooltipStyles={tooltipStyles} />
        )}
      </div>
    </>
  );
});
